export const KEEPALIVE_BUDGET_BYTES = 65536;

// Avoids a `declare global` block, which is disallowed in implementation files.

const hubspotWindow = () => window;
export const estimateBytes = data => {
  if (data == null) return 0;
  if (data instanceof Blob) return data.size;
  if (data instanceof ArrayBuffer) return data.byteLength;
  if (ArrayBuffer.isView(data)) return data.byteLength;
  if (data instanceof URLSearchParams) return estimateBytes(data.toString());
  if (data instanceof FormData) {
    let size = 0;
    data.forEach((value, key) => {
      size += estimateBytes(key);
      size += value instanceof Blob ? value.size : estimateBytes(value);
    });
    return size;
  }
  if (typeof data === 'string') {
    // eslint-disable-next-line compat/compat
    if (typeof TextEncoder !== 'undefined') {
      // eslint-disable-next-line compat/compat
      return new TextEncoder().encode(data).byteLength;
    }
    // Conservative fallback: assume 3 bytes per char (max UTF-8 code unit)
    return data.length * 3;
  }
  return 0;
};

// Splits a string into chunks where each chunk's UTF-8 byte length <= maxBytes.
// Preserves surrogate pairs. Returns an empty array for empty/null input.
export const splitStringByBytes = (str, maxBytes) => {
  if (!str || maxBytes <= 0) return [];

  // eslint-disable-next-line compat/compat
  if (typeof TextEncoder === 'undefined') {
    const charsPerChunk = Math.floor(maxBytes / 3);
    if (charsPerChunk <= 0) return [];
    const chunks = [];
    for (let i = 0; i < str.length; i += charsPerChunk) {
      chunks.push(str.slice(i, i + charsPerChunk));
    }
    return chunks;
  }

  // eslint-disable-next-line compat/compat
  const encoder = new TextEncoder();
  const chunks = [];
  let start = 0;
  while (start < str.length) {
    let end = start;
    let byteCount = 0;
    while (end < str.length) {
      const cp = str.codePointAt(end);
      const char = String.fromCodePoint(cp);
      const charBytes = encoder.encode(char).byteLength;
      if (byteCount + charBytes > maxBytes) break;
      byteCount += charBytes;
      end += char.length; // char.length is 2 for surrogate pairs
    }
    if (end === start) {
      // Single code point exceeds maxBytes; emit it as its own oversized chunk
      // rather than silently dropping the remaining input.
      const cp = str.codePointAt(end);
      end += String.fromCodePoint(cp).length;
    }
    chunks.push(str.slice(start, end));
    start = end;
  }
  return chunks;
};
const getBudgetState = () => {
  const win = hubspotWindow();
  if (!win.hubspot) {
    win.hubspot = {};
  }
  if (!win.hubspot._keepaliveBudget) {
    win.hubspot._keepaliveBudget = {
      inFlight: 0
    };
  }
  return win.hubspot._keepaliveBudget;
};
export const getKeepaliveBytesInFlight = () => getBudgetState().inFlight;
export const getRemainingKeepaliveBudget = () => Math.max(0, KEEPALIVE_BUDGET_BYTES - getKeepaliveBytesInFlight());
const resetBudgetState = () => {
  var _win$hubspot;
  const win = hubspotWindow();
  if ((_win$hubspot = win.hubspot) !== null && _win$hubspot !== void 0 && _win$hubspot._keepaliveBudget) {
    win.hubspot._keepaliveBudget.inFlight = 0;
  }
};

// Module-level queue; shared within this module instance.
// Cross-library coordination happens via window.hubspot._keepaliveBudget.
const pendingQueue = [];
let teardownRegistered = false;
let _isTeardown = false;
const adjustInFlight = delta => {
  const state = getBudgetState();
  state.inFlight = Math.max(0, state.inFlight + delta);
};
const tryProcessQueue = () => {
  if (_isTeardown) return;
  while (pendingQueue.length > 0) {
    const item = pendingQueue[0];
    if (item.size > getRemainingKeepaliveBudget()) break;
    pendingQueue.shift();
    dispatchItem(item); // eslint-disable-line @typescript-eslint/no-use-before-define
  }
};
const dispatchBeacon = (url, data, size, callbacks) => {
  adjustInFlight(size);
  // eslint-disable-next-line compat/compat
  const sent = navigator.sendBeacon(url, data);
  if (sent) {
    var _callbacks$onSuccess;
    if (!_isTeardown) {
      // Release immediately during normal browsing. sendBeacon provides no
      // completion callback, so any delay would be an arbitrary heuristic
      // rather than a real signal. The tradeoff: if a second large beacon
      // arrives within the same network RTT, the browser's actual budget may
      // still be consumed and its sendBeacon call will return false — but that
      // failure is surfaced via onFailure('BUDGET_EXCEEDED') rather than
      // silently dropped, so the caller can react. During teardown we hold
      // bytes instead (see flushQueueSynchronously), where the budget truly
      // isn't replenished before the page unloads.
      adjustInFlight(-size);
    }
    (_callbacks$onSuccess = callbacks.onSuccess) === null || _callbacks$onSuccess === void 0 || _callbacks$onSuccess.call(callbacks);
    tryProcessQueue();
  } else {
    var _callbacks$onFailure;
    // Browser rejected the beacon (budget exceeded); release our reservation.
    adjustInFlight(-size);
    (_callbacks$onFailure = callbacks.onFailure) === null || _callbacks$onFailure === void 0 || _callbacks$onFailure.call(callbacks, 'BUDGET_EXCEEDED');
  }
};
const dispatchFetch = (url, init, size, callbacks) => {
  adjustInFlight(size);
  fetch(url, Object.assign({}, init, {
    keepalive: true
  })).then(response => {
    adjustInFlight(-size);
    if (!response.ok) {
      var _callbacks$onFailure2;
      (_callbacks$onFailure2 = callbacks.onFailure) === null || _callbacks$onFailure2 === void 0 || _callbacks$onFailure2.call(callbacks, new Error(`HTTP ${response.status}`));
    } else {
      var _callbacks$onSuccess2;
      (_callbacks$onSuccess2 = callbacks.onSuccess) === null || _callbacks$onSuccess2 === void 0 || _callbacks$onSuccess2.call(callbacks);
    }
    tryProcessQueue();
  }).catch(err => {
    var _callbacks$onFailure3;
    adjustInFlight(-size);
    (_callbacks$onFailure3 = callbacks.onFailure) === null || _callbacks$onFailure3 === void 0 || _callbacks$onFailure3.call(callbacks, err);
    tryProcessQueue();
  });
};
const dispatchItem = item => {
  if (item.type === 'beacon') {
    dispatchBeacon(item.url, item.data, item.size, item.callbacks);
  } else {
    dispatchFetch(item.url, item.init, item.size, item.callbacks);
  }
};

// Drains the queue synchronously. Used at page teardown when Promises and
// setTimeout are suspended. Prefers sendBeacon for all pending items (including
// those originally queued as fetch keepalive) because it is the only mechanism
// guaranteed to be delivered across page navigation/close.
export const flushQueueSynchronously = () => {
  _isTeardown = true;
  while (pendingQueue.length > 0) {
    const item = pendingQueue.shift();
    if (item.size > getRemainingKeepaliveBudget()) {
      var _item$callbacks$onFai, _item$callbacks;
      (_item$callbacks$onFai = (_item$callbacks = item.callbacks).onFailure) === null || _item$callbacks$onFai === void 0 || _item$callbacks$onFai.call(_item$callbacks, 'TEARDOWN_BUDGET_EXCEEDED');
      continue;
    }
    let data;
    if (item.type === 'beacon') {
      data = item.data;
    } else {
      const body = item.init.body;
      // ReadableStream cannot be passed to sendBeacon and will throw or
      // silently fail in some browsers. Reject early rather than propagating.
      if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) {
        var _item$callbacks$onFai2, _item$callbacks2;
        (_item$callbacks$onFai2 = (_item$callbacks2 = item.callbacks).onFailure) === null || _item$callbacks$onFai2 === void 0 || _item$callbacks$onFai2.call(_item$callbacks2, 'TEARDOWN_BODY_UNSERIALIZABLE');
        continue;
      }
      data = body;
    }
    dispatchBeacon(item.url, data, item.size, item.callbacks);
  }
};
const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    flushQueueSynchronously();
  } else if (document.visibilityState === 'visible') {
    // Page is visible again; reset teardown state so normal queueing/dispatch
    // resumes. Also zero out inFlight: sendBeacon calls made during teardown
    // have either delivered or been abandoned. keepaliveFetch promises
    // in-flight at the time of the hidden event may not have settled yet —
    // their adjustInFlight(-size) calls will clamp to 0 via Math.max, so the
    // budget may temporarily under-count. Acceptable heuristic; tracking
    // unsettled promises across visibility transitions isn't worth the complexity.
    _isTeardown = false;
    resetBudgetState();
  }
};
export const registerTeardownHandlers = () => {
  if (teardownRegistered) return;
  teardownRegistered = true;
  window.addEventListener('beforeunload', flushQueueSynchronously);
  window.addEventListener('visibilitychange', onVisibilityChange);
};
const enqueue = item => {
  if (!teardownRegistered) registerTeardownHandlers();
  if (item.size > KEEPALIVE_BUDGET_BYTES) {
    var _item$callbacks$onFai3, _item$callbacks3;
    (_item$callbacks$onFai3 = (_item$callbacks3 = item.callbacks).onFailure) === null || _item$callbacks$onFai3 === void 0 || _item$callbacks$onFai3.call(_item$callbacks3, 'PAYLOAD_TOO_LARGE');
    return;
  }
  if (!_isTeardown && pendingQueue.length === 0 && item.size <= getRemainingKeepaliveBudget()) {
    dispatchItem(item);
    return;
  }
  pendingQueue.push(item);

  // If we're already in teardown (e.g. called from a library's own beforeunload
  // handler that fires after ours), flush synchronously now.
  if (_isTeardown) {
    flushQueueSynchronously();
  }
};
export const enqueueBeacon = (url, data, callbacks = {}) => {
  enqueue({
    type: 'beacon',
    url,
    data,
    size: estimateBytes(data),
    callbacks
  });
};
export const enqueueKeepaliveFetch = (url, init = {}, callbacks = {}) => {
  // ReadableStream bodies have no size signal and cannot be passed to sendBeacon
  // at teardown. Reject upfront rather than silently bypassing budget tracking.
  if (typeof ReadableStream !== 'undefined' && init.body instanceof ReadableStream) {
    var _callbacks$onFailure4;
    (_callbacks$onFailure4 = callbacks.onFailure) === null || _callbacks$onFailure4 === void 0 || _callbacks$onFailure4.call(callbacks, 'BODY_UNSERIALIZABLE');
    return;
  }
  enqueue({
    type: 'fetch',
    url,
    init,
    size: estimateBytes(init.body),
    callbacks
  });
};

// Resets all module-level state. Only for use in tests.
export const _resetForTesting = () => {
  pendingQueue.length = 0;
  if (teardownRegistered) {
    window.removeEventListener('beforeunload', flushQueueSynchronously);
    window.removeEventListener('visibilitychange', onVisibilityChange);
  }
  teardownRegistered = false;
  _isTeardown = false;
  resetBudgetState();
};